import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { imageApi } from '../services/api'
import { AnnotationStatus, SpaceType } from '../types'
import ImageGrid from '../components/ImageGrid'
import Pagination from '../components/Pagination'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'

interface ImageAnnotationPageProps {
  annotatorId: string
  onSearchChange?: (search: string) => void
  onStatusFilterChange?: (status: AnnotationStatus | 'all') => void
  onProjectFilterChange?: (project: string) => void
  currentSpace: SpaceType
  onApproverStatusFilterChange?: (status: string) => void
}

const ImageAnnotationPage = ({ annotatorId, onSearchChange, onStatusFilterChange, onProjectFilterChange, currentSpace, onApproverStatusFilterChange }: ImageAnnotationPageProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<AnnotationStatus | 'all'>('all')
  const [approverStatusFilter, setApproverStatusFilter] = useState<string>('all')
  const [projectFilter, setProjectFilter] = useState<string>('all')
  const [pageSize, setPageSize] = useState(20)
  const queryClient = useQueryClient()

  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1) // Reset to first page when searching
      onSearchChange?.(searchTerm)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm, onSearchChange])

  // Fetch available projects
  const { data: projects } = useQuery(
    ['projects', currentSpace],
    () => imageApi.getProjects(currentSpace),
    {
      staleTime: 300000, // 5 minutes
    }
  )

  // Fetch images with pagination
  const {
    data: paginatedData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['images', currentPage, pageSize, debouncedSearchTerm, statusFilter, approverStatusFilter, projectFilter, currentSpace],
    () => imageApi.getImages(
      currentPage, 
      pageSize, 
      debouncedSearchTerm || undefined, 
      currentSpace === 'annotator' ? statusFilter : undefined,
      projectFilter,
      currentSpace,
      currentSpace === 'approver' ? approverStatusFilter : undefined
    ),
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
    }
  )

  // No need for client-side filtering since it's handled by the API
  const filteredData = paginatedData
  const hasValidData = Array.isArray(filteredData?.data)
  const images = hasValidData ? filteredData!.data : []
  const totalCount = typeof filteredData?.total_count === 'number' ? filteredData.total_count : images.length
  const totalPages = typeof filteredData?.total_pages === 'number' ? filteredData.total_pages : 0
  const hasNext = typeof filteredData?.has_next === 'boolean' ? filteredData.has_next : false
  const hasPrev = typeof filteredData?.has_prev === 'boolean' ? filteredData.has_prev : false
  const unexpectedDataError = Boolean(filteredData && !hasValidData)

  const handleAnnotationUpdate = () => {
    // Invalidate and refetch the current page
    queryClient.invalidateQueries(['images'])
    queryClient.invalidateQueries(['annotation-stats'])
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
  }

  const handleStatusFilterChange = (status: AnnotationStatus | 'all') => {
    setStatusFilter(status)
    setCurrentPage(1)
    onStatusFilterChange?.(status)
  }

  const handleProjectFilterChange = (project: string) => {
    setProjectFilter(project)
    setCurrentPage(1)
    onProjectFilterChange?.(project)
  }

  const handleApproverStatusFilterChange = (status: string) => {
    setApproverStatusFilter(status)
    setCurrentPage(1)
    onApproverStatusFilterChange?.(status)
  }

  return (
    <div className="max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-8 py-4 md:py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Image Annotation
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Review and annotate images with accept/reject decisions and notes.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 md:mb-6 space-y-3 md:space-y-4 lg:space-y-0 lg:flex lg:items-center lg:justify-between">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1 max-w-lg">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-8 md:pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
              placeholder="Search by image ID or division..."
            />
          </div>
        </form>

        {/* Filters and Controls */}
        <div className="flex flex-wrap items-center gap-2 md:space-x-4">
          {/* Status Filter */}
          <div className="flex items-center space-x-1 md:space-x-2">
            <FunnelIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
            {currentSpace === 'annotator' ? (
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value as AnnotationStatus | 'all')}
                className="block px-2 py-1 md:px-3 md:py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="regenerate">Regenerate</option>
              </select>
            ) : (
              <select
                value={approverStatusFilter}
                onChange={(e) => handleApproverStatusFilterChange(e.target.value)}
                className="block px-2 py-1 md:px-3 md:py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
            )}
          </div>

          {/* Project Filter */}
          <div className="flex items-center space-x-1 md:space-x-2">
            <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300 hidden sm:inline">Project:</span>
            <select
              value={projectFilter}
              onChange={(e) => handleProjectFilterChange(e.target.value)}
              className="block px-2 py-1 md:px-3 md:py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="all">All Projects</option>
              {projects?.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          {/* Page Size */}
          <div className="flex items-center space-x-1 md:space-x-2">
            <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300 hidden sm:inline">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setCurrentPage(1)
              }}
              className="block px-2 py-1 md:px-3 md:py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Info */}
      {hasValidData && (
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Showing {images.length} of {totalCount} images in {currentSpace} space
          {debouncedSearchTerm && ` for "${debouncedSearchTerm}"`}
          {currentSpace === 'annotator' && statusFilter !== 'all' && ` with status "${statusFilter}"`}
          {currentSpace === 'approver' && approverStatusFilter !== 'all' && ` with approval status "${approverStatusFilter === 'completed' ? 'completed' : approverStatusFilter}"`}
          {projectFilter !== 'all' && ` from project "${projectFilter}"`}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="large" />
        </div>
      ) : error ? (
        <ErrorMessage 
          message="Failed to load images. Please try again." 
          onRetry={refetch}
        />
      ) : unexpectedDataError ? (
        <ErrorMessage 
          message="Unexpected data returned from the server. Please try again."
          onRetry={refetch}
        />
      ) : hasValidData && images.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">
            {debouncedSearchTerm || statusFilter !== 'all' || projectFilter !== 'all'
              ? 'No images match your search criteria.' 
              : 'No images found.'}
          </div>
        </div>
      ) : (
        <>
          {/* Image Grid */}
          {hasValidData && (
            <ImageGrid
              images={images}
              annotatorId={annotatorId}
              onAnnotationUpdate={handleAnnotationUpdate}
              currentSpace={currentSpace}
            />
          )}

          {/* Pagination - Show for all filters when there are multiple pages */}
          {hasValidData && totalPages > 1 && (
            <div className="mt-8">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                hasNext={hasNext}
                hasPrev={hasPrev}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ImageAnnotationPage
